/**
 * NexxusShell — coquille applicative en light DOM, fidèle à la charte v2.2.
 *
 * Rend la topbar marine, la sidebar 252px (accordéon), le footer et les
 * 32 sections `.view` (markup extrait verbatim de la référence v2.2), puis
 * pilote la navigation (affichage/masquage des vues, état actif, filtrage
 * des accès par rôle). Les données de chaque écran sont injectées par des
 * contrôleurs de vue branchés sur les services métier (Dexie) existants.
 *
 * Le CSS global (nexxus-theme.css) est importé ici une seule fois : il
 * s'applique donc en light DOM à toute l'application.
 */
import '@presentation/styles/nexxus-theme.css';
import shellHtml from '@presentation/templates/shell.html?raw';
import viewsHtml from '@presentation/templates/views.html?raw';
import loginHtml from '@presentation/templates/login.html?raw';
import licenseGateHtml from '@presentation/templates/license-gate.html?raw';
import { formatAppFooter, getAppVersion, sidebarFooterLines } from '@shared/constants/appVersion';
import { issuerContactEmail, issuerName } from '@domain/legal/IssuerInfo';
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';
import { RECETTE_BADGE, isRecetteBuild } from '@shared/constants/buildProfile';
import type { AppContext } from '@application/context/AppContext';
import type { Role, Session } from '@shared/types/User';
import { ToastService } from '@presentation/components/Toast';
import {
  installSearchableSelects,
  enhanceSearchableSelects,
} from '@presentation/components/SearchableSelect';
import { enhanceDataTables } from '@shared/a11y/enhanceDataTables';
import { ensureDocumentLang } from '@shared/a11y/ensureDocumentLang';
import { installGlobalStubs } from '@presentation/shell/globalStubs';
import { mountSyncRejectedStatus } from '@presentation/shell/SyncRejectedStatus';
import { mountHybridSyncStatus } from '@presentation/shell/HybridSyncStatus';
import {
  applyServerSessionToIdentityCache,
  isHybridAccountsMode,
  performHybridLogin,
} from '@application/auth/HybridAuthBootstrap';
import { notifyHybridSyncNotice } from '@presentation/shell/HybridSyncNotices';
import {
  bindHybridSyncToApp,
  getHybridSyncRuntime,
  readHybridSyncInput,
  setHybridSyncNoticeHandler,
  setHybridSyncServerSessionHandler,
} from '@application/sync/HybridSyncBootstrap';
import { logger } from '@infra/telemetry/Logger';
import { isViewAllowedForAccess } from '@application/access/ViewAccessPolicy';
import { LICENCE_UPDATED_EVENT } from '@application/licence/LicenceEntitlementService';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { AutoUpdateMonitor } from '@application/update/AutoUpdateMonitor';
import {
  assertTurnstileOk,
  mountTurnstile,
  shouldUseTurnstile,
} from '@presentation/shell/TurnstileGuard';
import { LicensingSessionService } from '@infra/licence/LicensingSessionService';
import { RemoteAuthService } from '@application/auth/RemoteAuthService';
import { AuthError, AuthService } from '@infra/auth/AuthService';
import { performAuthLogout } from '@application/auth/authBridge';
import {
  consumePendingCompanySwitch,
  readWebCompanies,
  setPendingCompanySwitch,
} from '@application/auth/WebCompanyChoice';
import { getTenantUserById } from '@application/auth/UserDirectoryService';
import { syncEntitlementsWhenOnline } from '@application/licence/LicenceEntitlementSync';
import { getDeviceLabel } from '@infra/persistence/DeviceLabelStore';
import { BusinessApiError } from '@infra/api/BusinessApiClient';
import { formatRemoteAuthError } from '@application/auth/remoteAuthErrors';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import {
  expiredLicenceCopy,
  openLicenceSubscribePage,
} from '@application/licence/LicenceExpiryCopy';
import { isValidBusinessEmail } from '@shared/validation/email';
import { validateFirstAdminInput } from '@application/auth/FirstAdminSetup';
import {
  isDemoSeedEnabled,
  isDemoSeedOptIn,
  setDemoSeedOptIn,
} from '@infra/licence/demoSeedConfig';
import {
  hasInstallDataConsent,
  setInstallDataConsent,
} from '@infra/licence/installConsentStore';
import {
  buildInstallDataPolicy,
  INSTALL_DATA_POLICY_TITLE,
  INSTALL_DATA_POLICY_VERSION,
} from '@domain/legal/InstallDataPolicy';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import {
  formatNetworkStatusLabel,
  NetworkStatusService,
} from '@infra/network/NetworkStatusService';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import {
  isSessionTenantMismatch,
  licenceRejectionMessage,
} from '@application/licence/LicenceAccountGuard';
import { SessionIdleService } from '@infra/auth/SessionIdleService';
import { runBackupExport, runBackupImport } from '@application/backup/BackupActions';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { renderPayrollParameterTokens } from '@shared/constants/PayrollParameters';
import { wireGlobalSearch } from '@presentation/shell/GlobalSearch';
import {
  purgeAllViewSections,
  resetViewSection,
  snapshotViews,
} from '@presentation/shell/viewDom';
import { CabinetPortfolioService } from '@application/licence/CabinetPortfolioService';
import logoNexxusUrl from '@presentation/assets/logo-nexxus.png';
import { maybeShowWelcomeModal } from '@presentation/shell/WelcomeModal';
import { ensureHybridBackupUiAfterTenantSwitch } from '@presentation/shell/PortfolioHybridBackupStatus';
import {
  webActivationErrorMessage,
  webFirstAdminRefusalMessage,
} from '@application/licence/CloudLicenceMessages';
import {
  bindLicenceReadOnlyBanner,
  type ReadOnlyBannerBinding,
} from '@presentation/shell/PersistentStatusBanner';
import {
  bindLicenseKeyInput,
  licenseKeyForSubmit,
} from '@presentation/shell/webLicenseKeyFormat';
import {
  webActivationStepHtml,
  webOfferSummaryHtml,
} from '@presentation/shell/webActivationMarkup';

/** Contrôleur d'écran : peuple la section `.view` correspondante. */
export type ViewController = (
  section: HTMLElement,
  ctx: AppContext,
  shell: NexxusShell,
) => void | Promise<void>;

/** Jeton de permission de la charte v2.2. */
type Perm = 'admin' | 'rh' | 'compta' | 'lecture' | 'salarie';

const ROLE_TO_PERM: Record<Role, Perm> = {
  super_admin: 'admin',
  admin: 'admin',
  rh: 'rh',
  comptable: 'compta',
  lecture: 'lecture',
  salarie: 'salarie',
};

const ROLE_LABEL: Record<Perm, string> = {
  admin: 'Administrateur',
  rh: 'Gestionnaire RH',
  compta: 'Comptable',
  lecture: 'Lecture seule',
  salarie: 'Salarié',
};

function applyBrandLogo(img: HTMLImageElement | null): void {
  if (!img) return;
  img.src = logoNexxusUrl;
  img.alt = 'Nexxus ERP';
}

/** Affiche le logo PNG Nexxus (pas de badge texte « Nexxus / ERP »). */
function applyLoginBrand(card: Element | null): void {
  if (!card) return;
  card.querySelector('.login-brand')?.remove();
  const logo = card.querySelector<HTMLImageElement>('img[id$="-logo"], img#login-logo');
  if (!logo) return;
  logo.hidden = false;
  applyBrandLogo(logo);
}

export class NexxusShell {
  private readonly controllers = new Map<string, ViewController>();
  private readonly viewSnapshots = new Map<string, string>();
  private currentView = '';
  private navSeq = 0;
  private idleLock: SessionIdleService | null = null;
  private autoUpdate: AutoUpdateMonitor | null = null;
  /** Cleanup indicateur sync refusées (Agent 5 — monté au boot, zone hors refreshAfterTenantSwitch). */
  private unmountSyncRejected: (() => void) | null = null;
  private unmountHybridSync: (() => void) | null = null;
  private unbindHybridSync: (() => void) | null = null;
  private readOnlyBanner: ReadOnlyBannerBinding | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly ctx: AppContext,
    private readonly session: Session,
  ) {}

  /** Enregistre le contrôleur de données d'un écran. */
  register(view: string, controller: ViewController): this {
    this.controllers.set(view, controller);
    return this;
  }

  registerAll(map: Record<string, ViewController>): this {
    for (const [view, ctrl] of Object.entries(map)) this.register(view, ctrl);
    return this;
  }

  get permission(): Perm {
    return ROLE_TO_PERM[this.session.role];
  }

  /** Session utilisateur montée (référence pour MAJ / droits). */
  get currentSession(): Session {
    return this.session;
  }

  /** Monte la coquille et lance la navigation initiale. */
  mount(): void {
    this.root.className = `app role-${this.permission}`;
    const renderedViews = renderPayrollParameterTokens(viewsHtml);
    this.root.innerHTML = `${shellHtml}\n<main class="main">\n${renderedViews}\n</main>`;
    this.viewSnapshots.clear();
    snapshotViews(this.root).forEach((html, id) => this.viewSnapshots.set(id, html));

    this.hydrateBrand();
    this.hydrateUser();
    this.hydrateFooter();
    this.hydrateSidebarCollapsed();
    this.refreshAccess();
    void this.refreshCabinetChrome();
    this.applyRemoteStatusBanner();
    this.readOnlyBanner?.dispose();
    this.readOnlyBanner = bindLicenceReadOnlyBanner(this.root, () => this.ctx.getLicenceRuntime());
    this.applyWelcomeExperience(true);
    void this.startAutoUpdateMonitor();
    this.wireGlobals();
    installGlobalStubs();
    ensureDocumentLang('fr');
    installSearchableSelects(this.root);
    enhanceDataTables(this.root);
    this.wireEvents();
    // Ancrage Agent 5 — SyncRejectedStatus : boot shell uniquement (pas refreshAfterTenantSwitch).
    // DOM : `#sync-rejected-indicator` (topbar .right). Rafraîchi via hydrateUser → événement root.
    this.unmountSyncRejected?.();
    this.unmountSyncRejected = mountSyncRejectedStatus({
      root: this.root,
      getTenantId: () =>
        this.ctx.tenantContext.tryGetTenant()?.id ??
        this.session.tenant_id ??
        this.ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
        null,
    });
    // Synchronisation Hybride : aucun effet hors licence « hybride » (Standalone / Cloud inchangés).
    this.unmountHybridSync?.();
    this.unbindHybridSync?.();
    setHybridSyncNoticeHandler(notifyHybridSyncNotice);
    setHybridSyncServerSessionHandler((serverSession) => void applyServerSessionToIdentityCache(this.ctx, serverSession));
    this.unmountHybridSync = mountHybridSyncStatus({
      root: this.root,
      runtime: getHybridSyncRuntime(),
      getInput: () => readHybridSyncInput(this.ctx),
    });
    this.unbindHybridSync = bindHybridSyncToApp(this.ctx);
    window.addEventListener(LICENCE_UPDATED_EVENT, () => {
      this.refreshAccess();
      void this.refreshCabinetChrome();
      this.applyWelcomeExperience(false);
      if (!this.isCurrentViewAccessible()) {
        void this.navigate(this.defaultView(), { replace: true });
      }
    });
    const searchCleanup = wireGlobalSearch(this.root, (view) => void this.navigate(view));
    if (!searchCleanup) {
      logger.warn({ event: 'global_search_unavailable', reason: 'missing_dom_nodes' });
    }
    this.startIdleLock();
    this.startLicenceWatch();
    try {
      const once = sessionStorage.getItem('nexxus_recovery_code_once');
      if (once) {
        sessionStorage.removeItem('nexxus_recovery_code_once');
        ToastService.info(
          `Code de récupération à noter : ${once} (mot de passe oublié en mode local)`,
        );
      }
    } catch {
      /* ignore */
    }

    const requested = this.viewFromHash();
    const gate = this.ctx.getLicenceRuntime()?.featureGate;
    const initial =
      requested && isViewAllowedForAccess(requested, this.permission, gate, this.accessOpts())
        ? requested
        : this.defaultView();
    void this.navigate(initial, { replace: true });
  }

  // ---------------------------------------------------------------------------
  // Rendu du shell
  // ---------------------------------------------------------------------------

  private hydrateBrand(): void {
    applyBrandLogo(this.root.querySelector<HTMLImageElement>('#topbar-logo'));
  }

  private hydrateUser(): void {
    const s = this.session;
    const perm = this.permission;
    const apply = (name: string, email: string, initials: string): void => {
      this.setText('#user-name', name || ROLE_LABEL[perm]);
      this.setText('#user-email', email);
      this.setText('#user-avatar', initials || '?');
      this.setText('#role-badge', ROLE_LABEL[perm]);
    };

    void getTenantUserById(this.ctx, s.user_id, s.tenant_id)
      .catch(() => undefined)
      .then((user) => {
        if (!user) {
          apply(s.role, '', '?');
          return;
        }
        const name = `${user.prenom} ${user.nom}`.trim();
        const initials = `${(user.prenom[0] ?? '').toUpperCase()}${(user.nom[0] ?? '').toUpperCase()}`;
        apply(name, user.email, initials || '?');
      });
    // Agent 5 — après bascule tenant (refreshAfterTenantSwitch appelle hydrateUser) : rafraîchir le compteur.
    this.root.dispatchEvent(new Event('nexxus:sync-rejected-refresh'));
  }

  private hydrateFooter(): void {
    const lines = sidebarFooterLines(getAppVersion(), isRecetteBuild() ? RECETTE_BADGE : null);
    this.setText('#sidebar-footer-brand', lines.brand);
    this.setText('#sidebar-footer-version', lines.version);
    if (isRecetteBuild()) document.title = `[RECETTE] ${document.title}`;
  }

  private static readonly SIDEBAR_COLLAPSED_KEY = 'nexxus.sidebar.collapsed';

  private hydrateSidebarCollapsed(): void {
    try {
      const collapsed = localStorage.getItem(NexxusShell.SIDEBAR_COLLAPSED_KEY) === '1';
      this.applySidebarCollapsed(collapsed);
    } catch {
      this.applySidebarCollapsed(false);
    }
  }

  private applySidebarCollapsed(collapsed: boolean): void {
    this.root.classList.toggle('sidebar-collapsed', collapsed);
    const label = collapsed ? 'Ouvrir la barre latérale' : 'Fermer la barre latérale';
    for (const id of ['#menu-toggle', '#sidebar-collapse-btn']) {
      const btn = this.root.querySelector<HTMLButtonElement>(id);
      if (!btn) continue;
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }
  }

  private setSidebarCollapsed(collapsed: boolean): void {
    this.applySidebarCollapsed(collapsed);
    try {
      localStorage.setItem(NexxusShell.SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* private mode / quota */
    }
  }

  /** Options d’accès vues (Cabinet multi-entités signé). */
  private accessOpts(): { cabinetMode: boolean } {
    return { cabinetMode: CabinetPortfolioService.isCabinetMode(this.ctx) };
  }

  /** Masque les liens selon le rôle et la licence (FeatureGate + profils ERP). */
  refreshAccess(): void {
    const perm = this.permission;
    const gate = this.ctx.getLicenceRuntime()?.featureGate;
    const opts = this.accessOpts();

    this.root.querySelectorAll<HTMLElement>('.sidebar a[data-view]').forEach((link) => {
      const view = link.dataset.view ?? '';
      link.style.display = isViewAllowedForAccess(view, perm, gate, opts) ? '' : 'none';
    });

    this.root.querySelectorAll<HTMLElement>('.sidebar .nav-group').forEach((group) => {
      const links = Array.from(group.querySelectorAll<HTMLElement>('a[data-view]'));
      const anyVisible = links.some((l) => l.style.display !== 'none');
      group.style.display = anyVisible ? '' : 'none';
      if (group.id === 'nav-group-cabinet') {
        const cabinetOk = opts.cabinetMode && anyVisible;
        group.hidden = !cabinetOk;
        group.style.display = cabinetOk ? '' : 'none';
      }
    });
  }

  /** Sélecteur topbar : uniquement licence Cabinet multi-entités (admin). */
  async refreshCabinetChrome(): Promise<void> {
    const wrap = this.root.querySelector<HTMLElement>('#tenant-switch');
    const select = this.root.querySelector<HTMLSelectElement>('#tenant-select');
    const label = this.root.querySelector<HTMLElement>('.tenant-switch-label');

    if (!wrap || !select) return;

    if (isBrowserCloudHost()) {
      this.renderWebCompanySwitch(wrap, select, label);
      return;
    }

    const cabinet = CabinetPortfolioService.isCabinetMode(this.ctx);
    if (!cabinet) {
      wrap.hidden = true;
      select.replaceChildren();
      return;
    }

    const list = await CabinetPortfolioService.listSummaries(this.ctx);
    // Afficher seulement s’il y a au moins 2 entreprises à basculer
    wrap.hidden = list.length < 2;
    if (list.length < 2) {
      select.replaceChildren();
      return;
    }
    let activeId =
      this.ctx.tenantContext.tryGetTenant()?.id ??
      this.ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
      '';
    try {
      const stored = localStorage.getItem('nexxus_active_tenant');
      if (stored && list.some((t) => t.id === stored) && !activeId) {
        activeId = stored;
      }
    } catch {
      /* ignore */
    }
    select.replaceChildren();
    for (const t of list) {
      const opt = document.createElement('option');
      opt.value = t.id;
      opt.textContent = t.nom;
      if (t.id === activeId) opt.selected = true;
      select.appendChild(opt);
    }
    const activeNom = list.find((t) => t.id === activeId)?.nom;
    if (label) {
      label.textContent = activeNom ? `Entreprise : ${activeNom}` : 'Entreprise cliente';
    }
    if (activeId) {
      try {
        localStorage.setItem('nexxus_active_tenant', activeId);
      } catch {
        /* ignore */
      }
    }
    select.onchange = () => {
      const next = select.value;
      if (!next || next === activeId) return;
      void (async () => {
        const result = await CabinetPortfolioService.switchToTenant(this.ctx, next);
        if (!result.ok) {
          ToastService.error(result.error);
          select.value = activeId;
          return;
        }
        try {
          localStorage.setItem('nexxus_active_tenant', next);
        } catch {
          /* ignore */
        }
        const nom = list.find((t) => t.id === next)?.nom ?? next;
        if (label) label.textContent = `Entreprise : ${nom}`;
        ToastService.success(`Entreprise ouverte : ${nom}`);
        await this.refreshAfterTenantSwitch();
        await this.navigate('dashboard', { replace: true });
      })();
    };
  }

  /**
   * Navigateur Cloud : une session = une entreprise. Le sélecteur propose les entreprises où le mot de passe saisi à
   * la connexion ouvre un compte ; choisir une autre entreprise FERME la session (serveur + cookies), efface les
   * données du navigateur puis recharge l'application sur l'écran de connexion (mot de passe à ressaisir) :
   * aucune session, aucun cache ni aucun écran ne survit au changement d'entreprise.
   */
  private renderWebCompanySwitch(
    wrap: HTMLElement,
    select: HTMLSelectElement,
    label: HTMLElement | null,
  ): void {
    const known = readWebCompanies();
    const activeId = this.ctx.auth.getSession()?.tenant_id ?? '';
    if (!known || !known.companies.some((c) => c.tenant_id === activeId)) {
      wrap.hidden = true;
      select.replaceChildren();
      return;
    }
    wrap.hidden = false;
    select.replaceChildren();
    for (const company of known.companies) {
      const option = document.createElement('option');
      option.value = company.tenant_id;
      option.textContent = company.name;
      option.selected = company.tenant_id === activeId;
      select.appendChild(option);
    }
    const activeName = known.companies.find((c) => c.tenant_id === activeId)?.name;
    if (label) label.textContent = activeName ? `Entreprise : ${activeName}` : 'Entreprise cliente';
    select.onchange = () => {
      const next = select.value;
      if (!next || next === activeId) return;
      setPendingCompanySwitch({ email: known.email, tenant_id: next, companies: known.companies });
      void (async () => {
        try {
          await recordAuditEvent(this.ctx, {
            action: 'logout',
            entity: 'session',
            description: 'Changement d’entreprise (déconnexion)',
          });
        } catch {
          /* audit best-effort */
        }
        await performAuthLogout(this.ctx);
        location.reload();
      })();
    };
  }

  async refreshAfterTenantSwitch(): Promise<void> {
    // C2 / BUG4 — purge DOM de tous les écrans (pas seulement le courant) :
    // un switch dossier ne doit laisser aucune donnée du tenant précédent visible.
    purgeAllViewSections(this.root, this.viewSnapshots);
    this.refreshAccess();
    await this.refreshCabinetChrome();
    this.hydrateUser();
    this.applyWelcomeExperience(false);
    // Hybride : consentement / bannière / assistant à chaque bascule (pas seulement au login).
    await ensureHybridBackupUiAfterTenantSwitch({ root: this.root, ctx: this.ctx });
  }

  private isCurrentViewAccessible(): boolean {
    if (!this.currentView) return true;
    const gate = this.ctx.getLicenceRuntime()?.featureGate;
    return isViewAllowedForAccess(this.currentView, this.permission, gate, this.accessOpts());
  }

  /** Bandeau d'état réseau (distant) + mode hors ligne visible aussi en local. */
  private applyRemoteStatusBanner(): void {
    const topbar = this.root.querySelector('.topbar .right');
    if (!topbar || topbar.querySelector('#remote-network-badge')) return;

    const badge = document.createElement('span');
    badge.id = 'remote-network-badge';
    badge.className = 'role-badge';
    badge.style.marginRight = '8px';
    badge.style.fontSize = '11px';
    topbar.insertBefore(badge, topbar.firstChild);

    const remote = isRemoteMultiStationMode(this.ctx.getLockedDataMode());

    const refresh = (): void => {
      void (async () => {
        const browserOnline = NetworkStatusService.isBrowserOnline();
        // Jamais « Hors connexion » si le navigateur signale online.
        if (browserOnline) {
          if (!remote) {
            badge.textContent = 'En ligne';
            badge.style.background = 'var(--teal)';
            badge.title = 'Connexion Internet disponible — mises à jour vérifiées automatiquement';
            return;
          }
          const reachable = await getBusinessApiClient().confirmWriteReady();
          badge.textContent = reachable ? 'Serveur connecté' : 'Serveur injoignable';
          badge.style.background = reachable ? 'var(--teal)' : '#B45309';
          badge.title = reachable
            ? 'Serveur métier joignable'
            : 'Serveur injoignable — travail en file locale si disponible';
          return;
        }
        badge.textContent = remote ? 'Mode hors ligne' : 'Hors ligne — données locales';
        badge.style.background = '#B45309';
        badge.title =
          'Vous pouvez continuer à travailler. Les mises à jour s’installeront à la prochaine connexion.';
      })();
    };
    refresh();
    NetworkStatusService.subscribe(() => refresh());
    window.setInterval(() => refresh(), 30_000);
  }

  private async startAutoUpdateMonitor(): Promise<void> {
    if (isRecetteBuild()) return;
    if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_VERSION_CHECK !== 'true') return;
    const current =
      (await window.electron?.getVersion?.()) ?? getAppVersion();
    this.autoUpdate?.stop();
    this.autoUpdate = new AutoUpdateMonitor(this.ctx, current, this.root, () => this.session);
    this.autoUpdate.start();
    const w = window as unknown as Record<string, unknown>;
    w['installPendingAppUpdate'] = () => this.autoUpdate?.installPending();
    w['upgradeToLatestRelease'] = async () => {
      if (this.autoUpdate?.getPending()?.is_outdated) {
        return this.autoUpdate.installPending();
      }
      const { UpdateCoordinator } = await import('@application/update/UpdateCoordinator');
      const coordinator = new UpdateCoordinator(this.ctx, current, () => this.session);
      return coordinator.upgradeToLatest();
    };
  }

  /** Synchronise le bandeau lecture seule avec l’état réel de la licence (restriction, expiration, lecture seule). */
  private refreshReadOnlyBanner(): void {
    this.readOnlyBanner?.refresh();
  }

  /** Bandeau expiration + modale 1er lancement (essai / premiers pas). */
  private applyWelcomeExperience(showModalIfNeeded: boolean): void {
    const main = this.root.querySelector('main.main');
    if (!main) return;

    main.querySelector('#nexxus-trial-banner')?.remove();
    main.querySelector('#nexxus-onboarding')?.remove();

    const runtime = this.ctx.getLicenceRuntime();
    const state = runtime?.getState();
    const claims = state?.claims;
    const nowSec = Math.floor(Date.now() / 1000);
    const expired = state?.errorCode === 'EXPIRED' || Boolean(claims && claims.exp <= nowSec);

    if (expired) {
      const copy = expiredLicenceCopy(Boolean(claims?.trial));
      const banner = document.createElement('div');
      banner.id = 'nexxus-trial-banner';
      banner.setAttribute('role', 'alert');
      banner.style.cssText =
        'margin:12px 16px 0;padding:12px 16px;border-radius:8px;background:#7f1d1d;color:#fff;font-size:13px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;justify-content:space-between;';
      banner.innerHTML = `<span>${copy.body}</span><button type="button" id="nexxus-trial-subscribe" class="btn btn-primary">${copy.cta}</button>`;
      banner.querySelector('#nexxus-trial-subscribe')?.addEventListener('click', () => {
        void openLicenceSubscribePage();
      });
      main.prepend(banner);
    }

    this.refreshReadOnlyBanner();

    const trial = Boolean(!expired && claims?.trial && state?.valid);
    const daysLeft =
      trial && claims ? Math.max(0, Math.ceil((claims.exp - nowSec) / 86400)) : undefined;
    const cabinetMode = CabinetPortfolioService.isCabinetMode(this.ctx);

    if (showModalIfNeeded) {
      maybeShowWelcomeModal(this.root, {
        trial: trial || Boolean(claims?.trial),
        daysLeft,
        expired,
        cabinetMode,
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  /** Écran par défaut selon le rôle (salarié -> espace personnel). */
  private defaultView(): string {
    return this.permission === 'salarie' ? 'dashboard' : 'dashboard';
  }

  private viewFromHash(): string {
    const raw = window.location.hash.replace(/^#\/?/, '').trim();
    if (!raw) return '';
    const view = raw.split('/')[0] ?? '';
    return this.root.querySelector(`#view-${view}`) ? view : '';
  }

  /** Recharge l’écran courant depuis les données persistées. */
  reload(): Promise<void> {
    if (!this.currentView) return Promise.resolve();
    return this.navigate(this.currentView, { replace: true });
  }

  async navigate(view: string, opts: { replace?: boolean } = {}): Promise<void> {
    const section = this.root.querySelector<HTMLElement>(`#view-${view}`);
    if (!section) {
      logger.warn({ event: 'nav_unknown_view', view });
      return;
    }
    const runtime = this.ctx.getLicenceRuntime();
    const gate = runtime?.featureGate;
    if (!isViewAllowedForAccess(view, this.permission, gate, this.accessOpts())) {
      logger.warn({ event: 'nav_forbidden', view, role: this.session.role });
      ToastService.warning('Vous n’avez pas accès à cet écran');
      const fallback = this.defaultView();
      if (view !== fallback) await this.navigate(fallback, { replace: true });
      return;
    }

    const seq = ++this.navSeq;

    try {
      this.root.querySelectorAll('.view.active').forEach((v) => v.classList.remove('active'));
      const snapshot = this.viewSnapshots.get(section.id) ?? section.innerHTML;
      const fresh = resetViewSection(section, snapshot);
      fresh.classList.add('active');

      this.root
        .querySelectorAll('.sidebar a[data-view]')
        .forEach((a) => a.classList.remove('active'));
      this.root
        .querySelectorAll('.sidebar .nav-group')
        .forEach((g) => g.classList.remove('has-active', 'open'));
      const link = this.root.querySelector<HTMLElement>(`.sidebar a[data-view="${view}"]`);
      if (link) {
        link.classList.add('active');
        const group = link.closest('.nav-group');
        if (group) {
          group.classList.add('has-active', 'open');
          group.classList.remove('closed');
        }
      }
      this.syncNavGroupAria();

      this.currentView = view;

      const target = `#/${view}`;
      if (window.location.hash !== target) {
        if (opts.replace) history.replaceState(null, '', target);
        else window.location.hash = target;
      }

      this.root.querySelector('.main')?.scrollTo?.({ top: 0 });
      window.scrollTo({ top: 0 });

      const controller = this.controllers.get(view);
      if (controller) {
        try {
          await controller(fresh, this.ctx, this);
          if (seq !== this.navSeq) return;
          enhanceSearchableSelects(fresh);
          enhanceDataTables(fresh);
        } catch (err) {
          logger.error({ event: 'view_controller_failed', view, error: String(err) });
          ToastService.error(`Erreur de chargement de l'écran « ${view} »`);
        }
      }
    } finally {
      if (seq === this.navSeq) this.closeSidebar();
    }
  }

  // ---------------------------------------------------------------------------
  // Événements & globals
  // ---------------------------------------------------------------------------

  private wireEvents(): void {
    // Délégation des clics sur tout élément portant data-view (sidebar + cartes)
    this.root.addEventListener('click', (e) => {
      const el = (e.target as HTMLElement)?.closest<HTMLElement>('[data-view]');
      if (el && this.root.contains(el)) {
        e.preventDefault();
        const view = el.dataset.view;
        if (view) void this.navigate(view);
      }
    });

    // Retour/avant navigateur
    window.addEventListener('hashchange', () => {
      const view = this.viewFromHash();
      if (view && view !== this.currentView) void this.navigate(view, { replace: true });
    });
  }

  private wireGlobals(): void {
    const w = window as unknown as Record<string, unknown>;
    w['navigate'] = (view: string) => void this.navigate(view);
    w['toggleSidebar'] = () => this.toggleSidebar();
    w['closeSidebar'] = () => this.closeSidebar();
    w['toggleNavGroup'] = (btn: HTMLElement) => this.toggleNavGroup(btn);
    w['openUserMenu'] = () => {
      /* menu utilisateur : géré par la déconnexion directe dans la topbar */
    };
    // Onglets (pattern .tabs/.tab + .tab-content) partagé par de nombreux écrans.
    w['switchTab'] = (btn: HTMLElement, tabId: string) => {
      const tabs = btn.parentElement;
      tabs?.querySelectorAll('.tab').forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      const section = btn.closest('.view') ?? document;
      section.querySelectorAll('.tab-content').forEach((c) => c.classList.remove('active'));
      section.querySelector(`#${tabId}`)?.classList.add('active');
    };
    // Sélecteur « Périodes existantes… » : applique la période choisie puis revient à l'entrée neutre (ancien code en ligne).
    w['setPeriodeDepuisSelect'] = (select: HTMLSelectElement) => {
      (w['setPeriode'] as ((value: string) => void) | undefined)?.(select.value);
      select.selectedIndex = 0;
    };
    w['doLogout'] = () => {
      void (async () => {
        try {
          await recordAuditEvent(this.ctx, {
            action: 'logout',
            entity: 'session',
            description: 'Déconnexion utilisateur',
          });
        } catch {
          /* audit best-effort */
        }
        await performAuthLogout(this.ctx);
        location.reload();
      })();
    };
    // Actions d'administration du tableau de bord (A-21)
    w['seedDemoData'] = () => {
      void (async () => {
        try {
          if (!isDemoSeedEnabled() && !isDemoSeedOptIn()) {
            ToastService.warning(
              'Cochez « données de démonstration » à l’activation, ou activez VITE_ENABLE_DEMO_SEED.',
            );
            return;
          }
          const { SeedDemoData } = await import('@infra/seed/SeedDemoData');
          const tenantId =
            this.ctx.tenantContext.tryGetTenant()?.id ??
            this.ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
          if (!tenantId) {
            ToastService.error('Aucune entreprise active');
            return;
          }
          const res = await SeedDemoData.seedIntoTenant(this.ctx, tenantId);
          setDemoSeedOptIn(false);
          ToastService.success(
            `Données démo chargées (${res.salaries_count} salariés, ${res.bulletins_count} bulletins).`,
          );
          location.reload();
        } catch (err) {
          ToastService.error(String((err as Error).message ?? err));
        }
      })();
    };
    w['exportBackup'] = () => {
      void runBackupExport(this.ctx);
    };
    w['importBackup'] = () => {
      void runBackupImport(this.ctx);
    };
    w['resetAll'] = () => {
      if (confirm('Réinitialiser toutes les données locales ? Action irréversible.')) {
        void this.ctx.localTables.resetAll().then(() => location.reload());
      }
    };
  }

  private startIdleLock(): void {
    this.idleLock?.stop();
    this.idleLock = new SessionIdleService(
      () => {
        ToastService.warning('Session verrouillée après inactivité');
        void performAuthLogout(this.ctx).then(() => location.reload());
      },
      () => {
        this.ctx.auth.touchSession();
      },
    );
    this.idleLock.start();
  }

  /** Revérifie la date d’expiration en cours de session (pas seulement au lancement). */
  private startLicenceWatch(): void {
    const runtime = this.ctx.getLicenceRuntime();
    if (!runtime) return;
    const tick = async (): Promise<void> => {
      const tenantId = this.ctx.tenantContext.tryGetTenant()?.id ?? '';
      const salariesCount = tenantId
        ? await this.ctx.localTables.countByTenant('salaries', tenantId)
        : 0;
      const prev = runtime.getState();
      const next = await runtime.revalidateWithTenant(tenantId, salariesCount);
      if (next.errorCode === 'EXPIRED' && prev.errorCode !== 'EXPIRED') {
        this.applyWelcomeExperience(false);
      }
      this.refreshReadOnlyBanner();
    };
    window.setInterval(() => void tick(), 5 * 60 * 1000);
    void tick();
  }

  private toggleSidebar(): void {
    if (window.matchMedia('(max-width: 992px)').matches) {
      this.root.querySelector('#sidebar')?.classList.toggle('open');
      this.root.querySelector('#sidebar-backdrop')?.classList.toggle('show');
      const open = this.root.querySelector('#sidebar')?.classList.contains('open') === true;
      const btn = this.root.querySelector<HTMLButtonElement>('#menu-toggle');
      if (btn) {
        const label = open ? 'Fermer la barre latérale' : 'Ouvrir la barre latérale';
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
        btn.setAttribute('aria-expanded', String(open));
      }
      return;
    }
    this.setSidebarCollapsed(!this.root.classList.contains('sidebar-collapsed'));
  }

  private closeSidebar(): void {
    // Fermeture du tiroir mobile uniquement — ne pas replier la sidebar desktop.
    this.root.querySelector('#sidebar')?.classList.remove('open');
    this.root.querySelector('#sidebar-backdrop')?.classList.remove('show');
    if (window.matchMedia('(max-width: 992px)').matches) {
      const btn = this.root.querySelector<HTMLButtonElement>('#menu-toggle');
      if (btn) {
        btn.setAttribute('aria-label', 'Ouvrir la barre latérale');
        btn.setAttribute('title', 'Ouvrir la barre latérale');
        btn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  /** Accordéon : ouvre le groupe cliqué, ferme les autres. */
  private toggleNavGroup(btn: HTMLElement): void {
    const group = btn.parentElement;
    if (!group) return;
    const isOpen = group.classList.contains('open');
    this.root.querySelectorAll('.sidebar .nav-group').forEach((g) => g.classList.remove('open'));
    if (isOpen) {
      group.classList.add('closed');
    } else {
      group.classList.add('open');
      group.classList.remove('closed');
    }
    this.syncNavGroupAria();
  }

  private syncNavGroupAria(): void {
    this.root.querySelectorAll('.sidebar .nav-group').forEach((g) => {
      const sectionBtn = g.querySelector('button.nav-section');
      if (sectionBtn) {
        sectionBtn.setAttribute('aria-expanded', String(g.classList.contains('open')));
      }
    });
  }

  private setText(selector: string, value: string): void {
    const el = this.root.querySelector(selector);
    if (el) el.textContent = value;
  }

  // ---------------------------------------------------------------------------
  // Écran de connexion
  // ---------------------------------------------------------------------------

  static renderLogin(root: HTMLElement, ctx: AppContext, onSuccess: () => void): void {
    root.className = '';
    root.innerHTML = loginHtml.replace('__APP_FOOTER__', formatAppFooter(2026, issuerName(getIssuerInfo())));

    applyBrandLogo(root.querySelector<HTMLImageElement>('#login-logo'));
    applyLoginBrand(root.querySelector('.login-card'));

    const emailInput = root.querySelector<HTMLInputElement>('#login-email');
    const pwdInput = root.querySelector<HTMLInputElement>('#login-password');
    const btn = root.querySelector<HTMLButtonElement>('#login-submit');
    const help = root.querySelector<HTMLElement>('#login-help');
    const isRemote = isRemoteMultiStationMode(ctx.getLockedDataMode());
    const remoteAuth = isRemote ? new RemoteAuthService() : null;
    // Offre Hybride : les comptes sont ceux du serveur (identité mémorisée pour ouvrir hors ligne).
    const isHybrid = !isRemote && isHybridAccountsMode(ctx);
    const cloudWeb = isBrowserCloudHost();
    const licenseField = root.querySelector<HTMLElement>('#login-license-field');
    const licenseInput = root.querySelector<HTMLInputElement>('#login-license-key');
    const companyField = root.querySelector<HTMLElement>('#login-company-field');
    const companySelect = root.querySelector<HTMLSelectElement>('#login-company');
    const activateWeb = root.querySelector<HTMLButtonElement>('#login-web-activate');
    if (cloudWeb && activateWeb) {
      activateWeb.hidden = false;
      activateWeb.addEventListener('click', () => {
        NexxusShell.renderWebActivation(root, ctx, onSuccess);
      });
    }
    /** Cabinet (navigateur) : liste des entreprises où ce mot de passe ouvre un compte, à choisir avant de se connecter. */
    const showCompanies = (
      companies: ReadonlyArray<{ tenant_id: string; name: string }>,
      selected?: string,
    ): void => {
      if (!companyField || !companySelect) return;
      companySelect.replaceChildren();
      for (const company of companies) {
        const option = document.createElement('option');
        option.value = company.tenant_id;
        option.textContent = company.name;
        if (company.tenant_id === selected) option.selected = true;
        companySelect.appendChild(option);
      }
      companyField.hidden = false;
      companySelect.focus();
    };
    if (cloudWeb) {
      // Bascule d'entreprise demandée depuis l'application : e-mail et entreprise présélectionnés, mot de passe à ressaisir.
      const pending = consumePendingCompanySwitch();
      if (pending && emailInput) {
        emailInput.value = pending.email;
        showCompanies(pending.companies, pending.tenant_id);
        pwdInput?.focus();
      }
    }
    let loginFailures = 0;
    let turnstileToken: string | null = null;
    let turnstileCtl: { reset: () => void } | null = null;

    const ensureTurnstile = async (): Promise<void> => {
      if (!shouldUseTurnstile()) return;
      if (loginFailures < 2 && !isRemote) return;
      let slot = root.querySelector<HTMLElement>('#login-turnstile');
      if (!slot) {
        slot = document.createElement('div');
        slot.id = 'login-turnstile';
        slot.style.margin = '12px 0';
        btn?.parentElement?.insertBefore(slot, btn);
      }
      turnstileCtl = await mountTurnstile(slot, (t) => {
        turnstileToken = t;
      });
    };

    const submit = async (): Promise<void> => {
      const email = emailInput?.value.trim() ?? '';
      const password = pwdInput?.value ?? '';
      if (!email || !password) {
        ToastService.warning('Veuillez saisir votre email et votre mot de passe');
        return;
      }
      if (!isValidBusinessEmail(email)) {
        ToastService.error('Adresse email invalide');
        return;
      }
      if (loginFailures >= 2 || (isRemote && shouldUseTurnstile())) {
        await ensureTurnstile();
        if (!assertTurnstileOk(turnstileToken)) {
          ToastService.warning('Confirmez que vous n’êtes pas un robot, puis réessayez.');
          return;
        }
      }
      try {
        const licenceTenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
        if (isRemote && remoteAuth) {
          const instalOk = await LicensingSessionService.ensureInstallationSession(licenceTenantId);
          if (!instalOk) {
            ToastService.error(
              'Session installation indisponible — vérifiez la connexion au serveur de licence',
            );
            return;
          }
          const licenseKey =
            cloudWeb && licenseField && !licenseField.hidden
              ? (licenseInput?.value.trim() ?? '')
              : '';
          const companyId =
            cloudWeb && companyField && !companyField.hidden ? (companySelect?.value ?? '') : '';
          await remoteAuth.login(email, password, licenseKey || undefined, companyId || undefined);
        } else if (isHybrid) {
          const mode = await performHybridLogin(ctx, {
            email,
            password,
            ...(licenceTenantId ? { tenantId: licenceTenantId } : {}),
          });
          if (mode === 'offline') {
            ToastService.info('Connexion hors ligne : votre compte a été vérifié sur ce poste.');
          }
        } else {
          await ctx.auth.login({
            email,
            password,
            ...(licenceTenantId ? { tenant_id: licenceTenantId } : {}),
          });
        }
        const session = ctx.auth.getSession();
        // Navigateur Cloud : l'entreprise n'est rattachée à la licence qu'après la connexion (droits issus de
        // /v1/auth/me) ; on compare donc à la valeur d'APRÈS connexion, pas au tenant provisoire d'avant.
        const licenceTenantAfterLogin = cloudWeb
          ? ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id
          : licenceTenantId;
        if (isSessionTenantMismatch(session?.tenant_id, licenceTenantAfterLogin)) {
          await performAuthLogout(ctx);
          ToastService.error(licenceRejectionMessage('TENANT_MISMATCH'));
          return;
        }
        await syncEntitlementsWhenOnline(ctx);
        loginFailures = 0;
        try {
          await recordAuditEvent(ctx, {
            action: 'login',
            entity: 'session',
            description: `Connexion réussie (${email})`,
            payload: { email },
          });
        } catch {
          /* audit best-effort */
        }
        ToastService.success('Connexion réussie');
        onSuccess();
      } catch (err) {
        if (err instanceof BusinessApiError && err.code === 'tenant_required') {
          const companies = Array.isArray(err.details?.['companies'])
            ? (err.details['companies'] as Array<{ tenant_id: string; name: string }>)
            : [];
          if (companies.length > 1 && companyField) {
            // Navigateur Cloud, cabinet : le mot de passe ouvre plusieurs entreprises, l'utilisateur choisit.
            showCompanies(companies);
            ToastService.info('Choisissez l’entreprise à ouvrir, puis validez.');
            return;
          }
          if (licenseField) {
            // Repli : la clé de licence désigne la bonne entreprise (champ annoncé par le déplacement du focus).
            licenseField.hidden = false;
            licenseInput?.focus();
          }
        }
        loginFailures += 1;
        turnstileToken = null;
        turnstileCtl?.reset();
        if (loginFailures >= 2) void ensureTurnstile();
        if (err instanceof AuthError && err.code === 'TOO_MANY_ATTEMPTS') {
          ToastService.error('Trop de tentatives. Réessayez dans 15 minutes.');
          return;
        }
        if (err instanceof BusinessApiError && !NetworkStatusService.isBrowserOnline()) {
          ToastService.error('Serveur métier injoignable — vérifiez votre connexion');
          return;
        }
        ToastService.error(formatRemoteAuthError(err, 'Email ou mot de passe incorrect'));
      }
    };

    btn?.addEventListener('click', (e) => {
      e.preventDefault();
      void submit();
    });
    pwdInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void submit();
    });
    root.querySelector('#forgot-identifier')?.addEventListener('click', () => {
      if (!help) return;
      help.hidden = false;
      help.textContent =
        'Votre identifiant est votre adresse email professionnelle. Pour protéger les comptes, l’application ne peut pas afficher la liste avant connexion. Contactez un administrateur de votre entreprise si vous ne la retrouvez pas.';
    });
    root.querySelector('#forgot-password')?.addEventListener('click', () => {
      if (!help) return;
      help.hidden = false;
      if (!isRemote) {
        NexxusShell.renderLocalPasswordReset(root, ctx, help, emailInput, onSuccess);
        return;
      }
      const email = emailInput?.value.trim() ?? '';
      if (!email) {
        help.textContent = 'Saisissez d’abord votre adresse email, puis relancez la demande.';
        emailInput?.focus();
        return;
      }
      help.textContent = 'Envoi de la demande…';
      void (async () => {
        const tenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
        const installationReady = await LicensingSessionService.ensureInstallationSession(tenantId);
        if (!installationReady) throw new Error('installation_session_unavailable');
        const licenseKey =
          cloudWeb && licenseField && !licenseField.hidden ? (licenseInput?.value.trim() ?? '') : '';
        await getBusinessApiClient().requestPasswordReset(email, licenseKey || undefined);
      })()
        .then(() => {
          help.textContent =
            'Si ce compte existe, un email contenant un lien valable 30 minutes vient d’être envoyé.';
        })
        .catch(() => {
          help.textContent =
            'La demande n’a pas pu être envoyée. Vérifiez la connexion internet puis réessayez.';
        });
    });
    if (AuthService.consumeSessionExpiredNotice()) {
      const notice = root.querySelector<HTMLElement>('#login-session-notice');
      if (notice) {
        notice.hidden = false;
        notice.textContent = 'Session expirée — veuillez vous reconnecter';
      } else {
        ToastService.warning('Session expirée — veuillez vous reconnecter');
      }
    }
    if (cloudWeb && licenseInput) {
      bindLicenseKeyInput(licenseInput);
    }
    emailInput?.focus();
  }

  /** Affiche le code de récupération une seule fois après création du 1er admin. */
  static showLocalRecoveryCode(
    root: HTMLElement,
    recoveryCode: string,
    onContinue: () => void,
  ): void {
    root.className = '';
    root.innerHTML = `
      <div class="login-page">
        <div class="login-card">
          <h1>Code de récupération</h1>
          <p>Notez ce code en lieu sûr. Il permet de réinitialiser votre mot de passe <strong>sans Internet</strong> (mode local).</p>
          <p id="recovery-code-value" style="font-size:1.4rem;font-weight:700;letter-spacing:0.08em;text-align:center;padding:16px;background:#f0faf8;border-radius:8px;color:#0a121e;"></p>
          <p style="font-size:12px;color:#586069;">Ce code ne sera plus réaffiché. Conservez-le comme un mot de passe.</p>
          <button type="button" class="btn btn-primary" id="recovery-continue" style="width:100%;margin-top:12px;justify-content:center;">J’ai noté mon code — continuer</button>
        </div>
      </div>`;
    const codeEl = root.querySelector('#recovery-code-value');
    if (codeEl) codeEl.textContent = recoveryCode;
    root.querySelector('#recovery-continue')?.addEventListener('click', () => onContinue());
  }

  /** Formulaire local : email auto par mail, ou code de récupération hors-ligne. */
  static renderLocalPasswordReset(
    root: HTMLElement,
    ctx: AppContext,
    help: HTMLElement,
    emailInput: HTMLInputElement | null,
    onSuccess: () => void,
  ): void {
    const prefill = emailInput?.value.trim() ?? '';
    help.innerHTML = `
      <strong>Réinitialisation locale</strong>
      <p style="margin:8px 0;">Un <strong>code est envoyé par email</strong> (Internet requis). Sans Internet, utilisez le code de récupération noté à la création du compte.</p>
      <div class="field" style="margin:8px 0;"><label>Email</label><input id="local-reset-email" type="email" value="${prefill.replace(/"/g, '&quot;')}"></div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin:8px 0;">
        <button type="button" class="btn btn-primary btn-sm" id="local-reset-send-email">Recevoir un code par email</button>
      </div>
      <div class="field" style="margin:8px 0;">
        <label>Type de code</label>
        <select id="local-reset-mode">
          <option value="support">Code reçu par email (NXU-…)</option>
          <option value="recovery">Code de récupération hors-ligne (XXXX-XXXX)</option>
        </select>
      </div>
      <div class="field" style="margin:8px 0;"><label id="local-reset-code-label">Code reçu par email</label><input id="local-reset-code" type="text" placeholder="NXU-XXXX-XXXX" autocomplete="off" spellcheck="false"></div>
      <div class="field" style="margin:8px 0;"><label>Nouveau mot de passe</label><input id="local-reset-password" type="password" minlength="8" autocomplete="new-password"></div>
      <div class="field" style="margin:8px 0;"><label>Confirmer</label><input id="local-reset-password2" type="password" minlength="8" autocomplete="new-password"></div>
      <button type="button" class="btn btn-primary btn-sm" id="local-reset-submit" style="margin-top:8px;">Réinitialiser mon mot de passe</button>
      <p id="local-reset-msg" style="margin:8px 0 0;font-size:12px;"></p>
    `;
    const modeSelect = help.querySelector<HTMLSelectElement>('#local-reset-mode');
    const codeLabel = help.querySelector('#local-reset-code-label');
    const codeInput = help.querySelector<HTMLInputElement>('#local-reset-code');
    const syncMode = (): void => {
      const support = modeSelect?.value !== 'recovery';
      if (codeLabel) {
        codeLabel.textContent = support ? 'Code reçu par email' : 'Code de récupération hors-ligne';
      }
      if (codeInput) {
        codeInput.placeholder = support ? 'NXU-XXXX-XXXX' : 'XXXX-XXXX';
      }
    };
    modeSelect?.addEventListener('change', syncMode);
    syncMode();

    help.querySelector('#local-reset-send-email')?.addEventListener('click', () => {
      void (async () => {
        const msg = help.querySelector('#local-reset-msg');
        const email =
          help.querySelector<HTMLInputElement>('#local-reset-email')?.value.trim() ?? '';
        if (!email) {
          if (msg) msg.textContent = 'Saisissez votre adresse email.';
          return;
        }
        const tenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
        if (!tenantId) {
          if (msg) {
            msg.textContent =
              'Licence introuvable sur ce poste. Utilisez le code de récupération hors-ligne ou contactez Nexxus.';
          }
          return;
        }
        if (msg) msg.textContent = 'Envoi du code…';
        try {
          const { requestSupportUnlockEmail } = await import(
            '@infra/licence/SupportUnlockClient'
          );
          await requestSupportUnlockEmail({ email, tenantId });
          if (modeSelect) modeSelect.value = 'support';
          syncMode();
          if (msg) {
            msg.textContent =
              'Si cette installation est éligible, un code vient d’être envoyé. Vérifiez votre boîte mail (et les indésirables), puis saisissez le code ci-dessous.';
          }
        } catch (err) {
          if (msg) {
            const raw = err instanceof Error ? err.message : String(err);
            if (raw === 'rate_limited') {
              msg.textContent = 'Trop de demandes. Réessayez dans une heure.';
            } else if (/404|not found|not_found|endpoint/i.test(raw)) {
              msg.textContent =
                'Le service d’email n’est pas encore disponible sur le serveur. Utilisez le code de récupération hors-ligne, ou contactez Nexxus / l’admin pour un code de déblocage.';
            } else {
              msg.textContent =
                'Envoi impossible. Vérifiez Internet, ou utilisez le code de récupération hors-ligne.';
            }
          }
        }
      })();
    });

    help.querySelector('#local-reset-submit')?.addEventListener('click', () => {
      void (async () => {
        const msg = help.querySelector('#local-reset-msg');
        const email =
          help.querySelector<HTMLInputElement>('#local-reset-email')?.value.trim() ?? '';
        const code = help.querySelector<HTMLInputElement>('#local-reset-code')?.value.trim() ?? '';
        const password = help.querySelector<HTMLInputElement>('#local-reset-password')?.value ?? '';
        const password2 =
          help.querySelector<HTMLInputElement>('#local-reset-password2')?.value ?? '';
        const mode = modeSelect?.value === 'recovery' ? 'recovery' : 'support';
        if (!email || !code || password.length < 8) {
          if (msg) msg.textContent = 'Complétez tous les champs (mot de passe min. 8 caractères).';
          return;
        }
        if (password !== password2) {
          if (msg) msg.textContent = 'Les deux mots de passe ne correspondent pas.';
          return;
        }
        try {
          const tenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
          let recoveryCode: string;
          if (mode === 'support') {
            const { verifySupportUnlockCode } = await import(
              '@infra/licence/SupportUnlockClient'
            );
            const result = await ctx.auth.resetPasswordWithSupportUnlock(
              email,
              code,
              password,
              tenantId,
              verifySupportUnlockCode,
            );
            recoveryCode = result.recoveryCode;
          } else {
            const result = await ctx.auth.resetPasswordWithRecoveryCode(
              email,
              code,
              password,
              tenantId,
            );
            recoveryCode = result.recoveryCode;
          }
          await ctx.auth.login({ email, password, tenant_id: tenantId });
          ToastService.success('Mot de passe réinitialisé');
          NexxusShell.showLocalRecoveryCode(root, recoveryCode, onSuccess);
        } catch (err) {
          const codeErr = err instanceof AuthError ? err.code : '';
          if (msg) {
            msg.textContent =
              codeErr === 'TOO_MANY_ATTEMPTS'
                ? 'Trop de tentatives. Réessayez dans 15 minutes.'
                : codeErr === 'NETWORK_REQUIRED'
                  ? 'Connexion Internet requise pour le code reçu par email.'
                  : mode === 'support'
                    ? 'Code invalide, expiré, ou email non administrateur.'
                    : 'Code ou email incorrect. Vérifiez votre code de récupération.';
          }
        }
      })();
    });
  }

  // ---------------------------------------------------------------------------
  // Navigateur Cloud : activation par clé de licence, puis premier administrateur
  // ---------------------------------------------------------------------------

  static renderWebActivation(root: HTMLElement, ctx: AppContext, onSuccess: () => void): void {
    root.className = '';
    root.innerHTML = `
      <div class="login-page" id="web-activate-page">
        <div class="login-card">
          <img id="web-activate-logo" alt="Nexxus ERP" hidden>
          <h1>Activer mon espace</h1>
          ${webActivationStepHtml(1)}
          <p>Saisissez la clé de licence reçue de Nexxus pour ouvrir l’espace de votre entreprise.</p>
          <form id="web-activate-form" novalidate>
            <div class="field">
              <label for="web-activate-key">Clé de licence</label>
              <input id="web-activate-key" type="text" required placeholder="NXS-XXXX-XXXX-XXXX-XXXX" autocomplete="off" spellcheck="false" inputmode="text" autocapitalize="characters">
              <p class="hint">Format attendu : NXS-XXXX-XXXX-XXXX-XXXX (majuscules automatiques, collage tolérant).</p>
            </div>
            <p id="web-activate-error" class="error-text" role="alert" hidden></p>
            <button type="submit" class="btn btn-primary" id="web-activate-submit" style="width:100%; margin-top: 10px; justify-content: center;">Vérifier la licence</button>
          </form>
          <div style="display:flex;justify-content:center;margin-top:14px"><button type="button" class="btn btn-ghost btn-sm" id="web-activate-back">Retour à la connexion</button></div>
        </div>
      </div>`;
    applyBrandLogo(root.querySelector<HTMLImageElement>('#web-activate-logo'));
    applyLoginBrand(root.querySelector('.login-card'));

    const keyInput = root.querySelector<HTMLInputElement>('#web-activate-key');
    if (keyInput) {
      bindLicenseKeyInput(keyInput);
      keyInput.focus();
    }

    const errorEl = root.querySelector<HTMLElement>('#web-activate-error');
    const showError = (message: string): void => {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
    };
    root.querySelector('#web-activate-back')?.addEventListener('click', () => {
      NexxusShell.renderLogin(root, ctx, onSuccess);
    });
    const licenseMessage = (err: unknown): string =>
      webActivationErrorMessage(err, formatRemoteAuthError(err, 'Impossible de vérifier la licence'));

    root.querySelector('#web-activate-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const licenseKey = licenseKeyForSubmit(keyInput?.value ?? '');
      if (!licenseKey) {
        showError('Saisissez votre clé de licence.');
        return;
      }
      void (async () => {
        try {
          const status = await getBusinessApiClient().webActivate(licenseKey);
          if (!status.needs_first_admin) {
            ToastService.info('Cet espace a déjà un administrateur — connectez-vous.');
            NexxusShell.renderLogin(root, ctx, onSuccess);
            return;
          }
          NexxusShell.renderWebFirstAdmin(root, ctx, licenseKey, onSuccess, {
            tier: status.tier,
            licenseExpiresAt: status.license_expires_at,
          });
        } catch (err) {
          showError(licenseMessage(err));
        }
      })();
    });
  }

  /** Navigateur Cloud : création du premier administrateur de l'entreprise désignée par la clé de licence. */
  static renderWebFirstAdmin(
    root: HTMLElement,
    ctx: AppContext,
    licenseKey: string,
    onSuccess: () => void,
    offer?: { tier?: string; licenseExpiresAt?: string },
  ): void {
    root.className = '';
    root.innerHTML = `
      <div class="login-page" id="first-admin-page">
        <div class="login-card">
          <img id="first-admin-logo" alt="Nexxus ERP" hidden>
          <h1>Premier administrateur</h1>
          ${webActivationStepHtml(2)}
          ${webOfferSummaryHtml(offer?.tier, offer?.licenseExpiresAt)}
          <p>Licence valide. Créez le compte administrateur de votre entreprise.</p>
          <form id="first-admin-form" novalidate>
          <div class="field"><label for="first-admin-prenom">Prénom</label><input id="first-admin-prenom" type="text" required minlength="2" autocomplete="given-name"></div>
          <div class="field"><label for="first-admin-nom">Nom</label><input id="first-admin-nom" type="text" required minlength="2" autocomplete="family-name"></div>
          <div class="field"><label for="first-admin-email">Email professionnel</label><input id="first-admin-email" type="email" required autocomplete="username"></div>
          <div class="field"><label for="first-admin-password">Mot de passe</label><input id="first-admin-password" type="password" required minlength="12" maxlength="128" autocomplete="new-password"><p class="hint">12 caractères minimum (une phrase de passe est idéale), unique à ce compte.</p></div>
          <div class="field"><label for="first-admin-password-confirm">Confirmer le mot de passe</label><input id="first-admin-password-confirm" type="password" required minlength="12" maxlength="128" autocomplete="new-password"></div>
          <p id="first-admin-error" class="error-text" role="alert" hidden></p>
          <button type="submit" class="btn btn-primary" id="first-admin-submit" style="width:100%; margin-top: 10px; justify-content: center;">Créer mon compte</button>
          </form>
        </div>
      </div>`;
    applyBrandLogo(root.querySelector<HTMLImageElement>('#first-admin-logo'));
    applyLoginBrand(root.querySelector('.login-card'));
    root.querySelector<HTMLInputElement>('#first-admin-prenom')?.focus();

    const remoteAuth = new RemoteAuthService();
    const errorEl = root.querySelector<HTMLElement>('#first-admin-error');
    const showError = (message: string): void => {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
    };
    const value = (selector: string): string =>
      root.querySelector<HTMLInputElement>(selector)?.value ?? '';

    root.querySelector('#first-admin-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const prenom = value('#first-admin-prenom').trim();
      const nom = value('#first-admin-nom').trim();
      const email = value('#first-admin-email').trim();
      const password = value('#first-admin-password');
      const invalid = validateFirstAdminInput({
        prenom,
        nom,
        email,
        password,
        passwordConfirm: value('#first-admin-password-confirm'),
      });
      if (invalid) {
        showError(invalid);
        return;
      }
      if (errorEl) errorEl.hidden = true;
      void (async () => {
        try {
          await remoteAuth.registerFirstAdminWithLicenseKey({
            licenseKey,
            email,
            password,
            nom,
            prenom,
          });
        } catch (err) {
          if (err instanceof BusinessApiError && err.statusCode === 409) {
            ToastService.warning('Un administrateur existe déjà — connectez-vous');
            NexxusShell.renderLogin(root, ctx, onSuccess);
            return;
          }
          if (err instanceof BusinessApiError && err.statusCode === 403) {
            showError(webFirstAdminRefusalMessage(err.code));
            return;
          }
          showError(formatRemoteAuthError(err, 'Impossible de créer le compte'));
          return;
        }
        try {
          await remoteAuth.login(email, password, licenseKey);
        } catch {
          ToastService.warning('Compte créé — connectez-vous avec vos identifiants');
          NexxusShell.renderLogin(root, ctx, onSuccess);
          return;
        }
        ToastService.success('Compte administrateur créé');
        onSuccess();
      })();
    });
  }

  // ---------------------------------------------------------------------------
  // Premier administrateur (licence réelle, tenant provisionné)
  // ---------------------------------------------------------------------------

  static renderFirstAdminSetup(
    root: HTMLElement,
    ctx: AppContext,
    tenantId: string,
    onSuccess: () => void,
  ): void {
    root.className = '';
    root.innerHTML = `
      <div class="login-page" id="first-admin-page">
        <div class="login-card">
          <img id="first-admin-logo" alt="Nexxus ERP" hidden>
          <h1>Premier administrateur</h1>
          <p>Créez le compte administrateur de cette installation.</p>
          <form id="first-admin-form" novalidate>
          <div class="field"><label for="first-admin-prenom">Prénom</label><input id="first-admin-prenom" type="text" required minlength="2" autocomplete="given-name"></div>
          <div class="field"><label for="first-admin-nom">Nom</label><input id="first-admin-nom" type="text" required minlength="2" autocomplete="family-name"></div>
          <div class="field"><label for="first-admin-email">Email professionnel</label><input id="first-admin-email" type="email" required autocomplete="username"></div>
          <div class="field"><label for="first-admin-password">Mot de passe</label><input id="first-admin-password" type="password" required minlength="12" maxlength="128" autocomplete="new-password"><p class="hint">12 caractères minimum (une phrase de passe est idéale), unique à ce compte.</p></div>
          <div class="field"><label for="first-admin-password-confirm">Confirmer le mot de passe</label><input id="first-admin-password-confirm" type="password" required minlength="12" maxlength="128" autocomplete="new-password"></div>
          <p id="first-admin-error" class="error-text" role="alert" hidden></p>
          <button type="submit" class="btn btn-primary" id="first-admin-submit" style="width:100%; margin-top: 10px; justify-content: center;">Créer mon compte</button>
          </form>
        </div>
      </div>`;

    applyBrandLogo(root.querySelector<HTMLImageElement>('#first-admin-logo'));
    applyLoginBrand(root.querySelector('.login-card'));

    const isRemote = isRemoteMultiStationMode(ctx.getLockedDataMode());
    const isHybrid = !isRemote && isHybridAccountsMode(ctx);
    const remoteAuth = isRemote || isHybrid ? new RemoteAuthService() : null;

    const errorEl = root.querySelector<HTMLElement>('#first-admin-error');
    const showError = (message: string): void => {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
      ToastService.warning(message);
    };

    const submit = async (): Promise<void> => {
      const prenom =
        root.querySelector<HTMLInputElement>('#first-admin-prenom')?.value.trim() ?? '';
      const nom = root.querySelector<HTMLInputElement>('#first-admin-nom')?.value.trim() ?? '';
      const email = root.querySelector<HTMLInputElement>('#first-admin-email')?.value.trim() ?? '';
      const password = root.querySelector<HTMLInputElement>('#first-admin-password')?.value ?? '';
      const passwordConfirm =
        root.querySelector<HTMLInputElement>('#first-admin-password-confirm')?.value ?? '';
      const invalid = validateFirstAdminInput({
        prenom,
        nom,
        email,
        password,
        passwordConfirm,
      });
      if (invalid) {
        showError(invalid);
        return;
      }
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      try {
        if ((isRemote || isHybrid) && remoteAuth) {
          const licenceTenantId = ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id;
          const instalOk = await LicensingSessionService.ensureInstallationSession(licenceTenantId);
          if (!instalOk) {
            ToastService.error(
              isHybrid
                ? 'Connexion Internet requise pour créer le premier compte de l’offre Hybride.'
                : 'Session installation indisponible',
            );
            return;
          }
          await remoteAuth.registerFirstAdmin(email, password, nom, prenom, 'admin');
          try {
            if (isHybrid) {
              await performHybridLogin(ctx, { email, password, ...(licenceTenantId ? { tenantId: licenceTenantId } : {}) });
            } else {
              await remoteAuth.login(email, password);
            }
          } catch {
            ToastService.warning('Compte créé — connectez-vous avec vos identifiants');
            NexxusShell.renderLogin(root, ctx, onSuccess);
            return;
          }
        } else {
          const sameTenant = await ctx.auth.findUserByEmail(email, tenantId);
          let recoveryCode: string | undefined;
          if (!sameTenant) {
            const created = await ctx.auth.register(
              tenantId,
              email,
              password,
              nom,
              prenom,
              'admin',
            );
            recoveryCode = created.recoveryCode;
          }
          await ctx.auth.login({ email, password, tenant_id: tenantId });
          if (recoveryCode) {
            NexxusShell.showLocalRecoveryCode(root, recoveryCode, onSuccess);
            return;
          }
        }
        ToastService.success('Compte administrateur créé');
        onSuccess();
      } catch (err) {
        if (err instanceof BusinessApiError && err.statusCode === 409) {
          ToastService.warning('Un administrateur existe déjà — connectez-vous');
          NexxusShell.renderLogin(root, ctx, onSuccess);
          return;
        }
        if (err instanceof Error && err.message === 'EMAIL_ALREADY_REGISTERED') {
          ToastService.error(
            'Un compte existe déjà avec cet email. Connectez-vous ou choisissez un autre email.',
          );
          return;
        }
        ToastService.error(formatRemoteAuthError(err, 'Impossible de créer le compte'));
      }
    };

    root.querySelector('#first-admin-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      void submit();
    });
  }

  // ---------------------------------------------------------------------------
  // Écran d'activation licence
  // ---------------------------------------------------------------------------

  static renderLicenseGate(
    root: HTMLElement,
    onActivate: (commercialKey: string, deviceLabel?: string) => Promise<string | null>,
    onImportFile?: (fileText: string) => Promise<string | null>,
  ): void {
    root.className = '';
    root.innerHTML = licenseGateHtml;

    applyBrandLogo(root.querySelector<HTMLImageElement>('#license-gate-logo-img'));

    const input = root.querySelector<HTMLTextAreaElement | HTMLInputElement>('#license-gate-input');
    const deviceInput = root.querySelector<HTMLInputElement>('#license-gate-device');
    const btn = root.querySelector<HTMLButtonElement>('#license-gate-submit');
    const msg = root.querySelector<HTMLParagraphElement>('#license-gate-msg');
    const networkEl = root.querySelector<HTMLParagraphElement>('#license-gate-network');
    const transferToggle = root.querySelector<HTMLButtonElement>('#license-gate-transfer-toggle');
    const transferPanel = root.querySelector<HTMLElement>('#license-gate-transfer-panel');
    const fingerprintEl = root.querySelector<HTMLElement>('#license-gate-fingerprint');
    const copyFingerprintButton = root.querySelector<HTMLButtonElement>(
      '#license-gate-copy-fingerprint',
    );
    const demoSeedCheckbox = root.querySelector<HTMLInputElement>('#license-gate-demo-seed');
    if (demoSeedCheckbox) {
      demoSeedCheckbox.checked = isDemoSeedOptIn();
      demoSeedCheckbox.addEventListener('change', () => {
        setDemoSeedOptIn(demoSeedCheckbox.checked);
      });
    }

    // Contact de l'éditeur (informations de l'émetteur, copie signée en cache) : DOM, jamais de HTML injecté.
    const contactHost = root.querySelector('#license-gate-contact');
    const issuerInfo = getIssuerInfo();
    if (contactHost && (issuerContactEmail(issuerInfo) || issuerInfo.website)) {
      contactHost.append(document.createElement('br'));
      if (issuerContactEmail(issuerInfo)) {
        const mail = document.createElement('a');
        mail.href = `mailto:${issuerContactEmail(issuerInfo)}`;
        mail.textContent = issuerContactEmail(issuerInfo);
        mail.style.cssText = 'color:var(--teal);font-weight:700;';
        contactHost.append(mail);
      }
      if (/^https?:\/\//i.test(issuerInfo.website)) {
        if (issuerContactEmail(issuerInfo)) contactHost.append(' · ');
        const site = document.createElement('a');
        site.href = issuerInfo.website;
        site.target = '_blank';
        site.rel = 'noopener';
        site.textContent = issuerInfo.website;
        site.style.cssText = 'color:var(--teal);font-weight:700;';
        contactHost.append(site);
      }
    }

    const policyTitle = root.querySelector('#license-gate-policy-title');
    const policyBody = root.querySelector('#license-gate-policy-body');
    const policyAccept = root.querySelector<HTMLInputElement>('#license-gate-policy-accept');
    if (policyTitle) policyTitle.textContent = INSTALL_DATA_POLICY_TITLE;
    if (policyBody) {
      policyBody.textContent = buildInstallDataPolicy(getIssuerInfo());
    }
    if (policyAccept && hasInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
      policyAccept.checked = true;
    }

    // Collage / clic droit : laisser le navigateur / Electron gérer (menu Édition).
    input?.addEventListener('paste', (ev) => {
      const pasteEvent = ev as ClipboardEvent;
      const paste = pasteEvent.clipboardData?.getData('text');
      if (!paste || !input) return;
      // Normalise les clés collées avec retours à la ligne
      const cleaned = paste.replace(/\s+/g, '').trim();
      if (cleaned && cleaned !== paste) {
        pasteEvent.preventDefault();
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        input.value = input.value.slice(0, start) + cleaned + input.value.slice(end);
      }
    });

    const refreshNetwork = (): void => {
      if (!networkEl) return;
      const online = NetworkStatusService.isBrowserOnline();
      networkEl.textContent = formatNetworkStatusLabel(online);
      networkEl.style.color = online ? 'var(--teal)' : '#B45309';
    };
    refreshNetwork();
    NetworkStatusService.subscribe(refreshNetwork);

    let deviceFingerprint = '';
    let deviceLabelLoaded = false;
    const loadDeviceLabel = async (): Promise<void> => {
      if (deviceLabelLoaded || !deviceInput) return;
      deviceLabelLoaded = true;
      const savedLabel = await getDeviceLabel();
      if (savedLabel !== 'Poste non nommé') deviceInput.value = savedLabel;
    };

    const loadFingerprint = async (): Promise<void> => {
      if (deviceFingerprint || !fingerprintEl) return;
      fingerprintEl.textContent = 'Chargement…';
      try {
        const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
        if (!fingerprint) throw new Error('device_fingerprint_unavailable');
        deviceFingerprint = fingerprint;
        fingerprintEl.textContent = deviceFingerprint;
        copyFingerprintButton?.removeAttribute('disabled');
      } catch {
        fingerprintEl.textContent =
          'Empreinte indisponible. Redémarrez l’application ou contactez le support.';
        copyFingerprintButton?.setAttribute('disabled', 'true');
      }
    };

    transferToggle?.addEventListener('click', () => {
      if (!transferPanel) return;
      const willOpen = transferPanel.hidden;
      transferPanel.hidden = !willOpen;
      transferToggle.setAttribute('aria-expanded', String(willOpen));
      transferToggle.textContent = willOpen ? 'Masquer' : 'Transférer';
      if (willOpen) {
        void loadDeviceLabel();
        void loadFingerprint();
      }
    });

    copyFingerprintButton?.addEventListener('click', () => {
      if (!deviceFingerprint) return;
      void navigator.clipboard
        .writeText(deviceFingerprint)
        .then(() => ToastService.success('Empreinte copiée'))
        .catch(() => ToastService.error('Impossible de copier l’empreinte'));
    });

    // Poste sans Internet : import d'un fichier de licence signé (vérifié localement).
    const offlineToggle = root.querySelector<HTMLButtonElement>('#license-gate-offline-toggle');
    const offlinePanel = root.querySelector<HTMLElement>('#license-gate-offline-panel');
    const offlineFingerprintEl = root.querySelector<HTMLElement>('#license-gate-offline-fingerprint');
    const offlineCopy = root.querySelector<HTMLButtonElement>('#license-gate-offline-copy');
    const offlineFile = root.querySelector<HTMLInputElement>('#license-gate-offline-file');
    const offlineImport = root.querySelector<HTMLButtonElement>('#license-gate-offline-import');
    const offlineMsg = root.querySelector<HTMLElement>('#license-gate-offline-msg');
    if (!onImportFile) {
      offlineToggle?.closest('.license-gate-transfer-prompt')?.remove();
      offlinePanel?.remove();
    }
    offlineToggle?.addEventListener('click', () => {
      if (!offlinePanel) return;
      const willOpen = offlinePanel.hidden;
      offlinePanel.hidden = !willOpen;
      offlineToggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        void loadFingerprint().then(() => {
          if (offlineFingerprintEl) offlineFingerprintEl.textContent = deviceFingerprint || '—';
          if (deviceFingerprint) offlineCopy?.removeAttribute('disabled');
        });
      }
    });
    offlineCopy?.addEventListener('click', () => {
      if (!deviceFingerprint) return;
      void navigator.clipboard
        .writeText(deviceFingerprint)
        .then(() => ToastService.success('Empreinte copiée'))
        .catch(() => ToastService.error('Impossible de copier l’empreinte'));
    });
    offlineImport?.addEventListener('click', () => {
      void (async () => {
        const file = offlineFile?.files?.[0];
        if (!file) {
          if (offlineMsg) offlineMsg.textContent = 'Choisissez d’abord le fichier de licence (.nxlic).';
          ToastService.warning('Choisissez le fichier de licence (.nxlic)');
          return;
        }
        if (!policyAccept?.checked) {
          if (offlineMsg) {
            offlineMsg.textContent =
              'Cochez d’abord la case d’acceptation de la politique de collecte de données.';
          }
          ToastService.warning(
            'Veuillez accepter la politique de collecte de données pour activer l’application',
          );
          policyAccept?.focus();
          return;
        }
        if (file.size > 64 * 1024) {
          if (offlineMsg) offlineMsg.textContent = 'Ce fichier est trop volumineux pour être un fichier de licence.';
          return;
        }
        offlineImport.setAttribute('disabled', 'true');
        if (offlineMsg) offlineMsg.textContent = 'Vérification du fichier en cours…';
        try {
          if (!setInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
            ToastService.error('Impossible d’enregistrer le consentement à la politique de données.');
            return;
          }
          const err = await onImportFile?.(await file.text());
          if (err) {
            if (offlineMsg) offlineMsg.textContent = err;
            ToastService.error(err);
            return;
          }
          ToastService.success('Fichier de licence importé');
          location.reload();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Import impossible';
          if (offlineMsg) offlineMsg.textContent = message;
          ToastService.error(message);
        } finally {
          offlineImport.removeAttribute('disabled');
        }
      })();
    });

    const submit = async (): Promise<void> => {
      const key = input?.value.trim() ?? '';
      if (!key) {
        ToastService.warning('Veuillez saisir votre clé commerciale');
        return;
      }
      if (!policyAccept?.checked) {
        ToastService.warning(
          'Veuillez accepter la politique de collecte de données pour activer l’application',
        );
        policyAccept?.focus();
        return;
      }
      btn?.setAttribute('disabled', 'true');
      if (btn) btn.textContent = 'Activation en cours…';
      try {
        if (!setInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
          ToastService.error('Impossible d’enregistrer le consentement à la politique de données.');
          return;
        }
        const err = await onActivate(key, deviceInput?.value.trim() || undefined);
        if (err) {
          if (msg) msg.textContent = err;
          ToastService.error(err);
          return;
        }
        ToastService.success('Activation réussie');
        location.reload();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Activation échouée';
        if (msg) msg.textContent = message;
        ToastService.error(message);
      } finally {
        btn?.removeAttribute('disabled');
        if (btn) btn.textContent = "Activer l'application";
      }
    };

    btn?.addEventListener('click', (e) => {
      e.preventDefault();
      void submit();
    });
    input?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void submit();
    });
    input?.focus();
  }
}
